import { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { resendReservationCode, verifyReservationCode } from "../services/reservationRequest";
import { extractApiErrorMessage } from "../utils/apiError";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 120;

interface OtpVerificationProps {
  reservationId: string;
  codeLifetimeMinutes: number;
  contactPhone?: string;
  onVerified: () => void;
  onCancel: () => void;
}

const formatTime = (totalSeconds: number) => {
  const m = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(totalSeconds % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
};

export default function OtpVerification({
  reservationId,
  codeLifetimeMinutes,
  contactPhone,
  onVerified,
  onCancel,
}: OtpVerificationProps) {
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendInfo, setResendInfo] = useState("");
  const [expirySeconds, setExpirySeconds] = useState(codeLifetimeMinutes * 60);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const timer = setInterval(() => {
      setExpirySeconds((s) => (s > 0 ? s - 1 : 0));
      setResendCooldown((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const isExpired = expirySeconds <= 0;
  const canResend = resendCooldown <= 0;
  const code = digits.join("");

  const focusInput = (index: number) => {
    inputRefs.current[index]?.focus();
    inputRefs.current[index]?.select();
  };

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    setError("");
    if (digit && index < CODE_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    e.preventDefault();
    const next = Array(CODE_LENGTH).fill("");
    pasted
      .slice(0, CODE_LENGTH)
      .split("")
      .forEach((char, i) => {
        next[i] = char;
      });
    setDigits(next);
    setError("");
    const lastFilled = Math.min(pasted.length, CODE_LENGTH) - 1;
    focusInput(lastFilled >= 0 ? lastFilled : 0);
  };

  const handleVerify = async () => {
    if (code.length !== CODE_LENGTH || verifying) return;
    setVerifying(true);
    setError("");
    setResendInfo("");
    try {
      await verifyReservationCode({ reservationId, code });
      onVerified();
    } catch (err) {
      setError(extractApiErrorMessage(err, "Doğrulama sırasında bir sorun oluştu. Lütfen tekrar deneyin."));
      setDigits(Array(CODE_LENGTH).fill(""));
      focusInput(0);
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || resending) return;
    setResending(true);
    setError("");
    setResendInfo("");
    try {
      const res = await resendReservationCode({ reservationId });
      const newLifetime = res.data?.codeLifetimeMinutes ?? codeLifetimeMinutes;
      setExpirySeconds(newLifetime * 60);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setDigits(Array(CODE_LENGTH).fill(""));
      setResendInfo("Yeni doğrulama kodu gönderildi.");
      focusInput(0);
    } catch (err) {
      setError(extractApiErrorMessage(err, "Kod tekrar gönderilemedi. Lütfen biraz sonra tekrar deneyin."));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="rr-otp-card">
      <i className="bi bi-shield-lock-fill rr-otp-icon"></i>
      <h3 className="rr-otp-title">Doğrulama Kodu</h3>
      <p className="rr-otp-subtitle">
        {contactPhone ? (
          <>
            <strong>{contactPhone}</strong> numarasına gönderilen 6 haneli kodu girin.
          </>
        ) : (
          "Telefonunuza gönderilen 6 haneli kodu girin."
        )}
      </p>

      <div className="rr-otp-inputs" onPaste={handlePaste}>
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            className="rr-otp-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={digit}
            disabled={verifying}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
          />
        ))}
      </div>

      <div className={`rr-otp-timer ${isExpired ? "expired" : ""}`}>
        {isExpired ? (
          <span>
            <i className="bi bi-exclamation-circle-fill me-1"></i>Kodun süresi doldu
          </span>
        ) : (
          <span>
            <i className="bi bi-clock-fill me-1"></i>Kalan süre: {formatTime(expirySeconds)}
          </span>
        )}
      </div>

      {error && (
        <div className="rr-otp-message error">
          <i className="bi bi-exclamation-circle-fill me-1"></i>
          {error}
        </div>
      )}
      {!error && resendInfo && (
        <div className="rr-otp-message success">
          <i className="bi bi-check-circle-fill me-1"></i>
          {resendInfo}
        </div>
      )}

      <button
        type="button"
        className="rr-btn-gold rr-otp-verify-btn"
        disabled={code.length !== CODE_LENGTH || verifying}
        onClick={handleVerify}
      >
        {verifying ? (
          <span className="spinner-border spinner-border-sm" role="status" />
        ) : (
          <i className="bi bi-check2-circle"></i>
        )}
        {verifying ? "Doğrulanıyor..." : "Kodu Doğrula"}
      </button>

      <div className="rr-otp-resend">
        {canResend ? (
          <button type="button" className="rr-otp-resend-btn" onClick={handleResend} disabled={resending}>
            {resending ? (
              <span className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <i className="bi bi-arrow-repeat"></i>
            )}
            {resending ? "Gönderiliyor..." : "Kodu Tekrar Gönder"}
          </button>
        ) : (
          <span className="rr-otp-resend-wait">
            Tekrar gönder: <strong>{formatTime(resendCooldown)}</strong>
          </span>
        )}
      </div>

      <button type="button" className="rr-otp-cancel" onClick={onCancel}>
        <i className="bi bi-arrow-left"></i> Bilgileri Değiştir
      </button>
    </div>
  );
}
